import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { useSignOut } from '@kss/auth';
import { cn } from '../../utils/cn';
import type { NavItem } from '../../types';

interface IconRailProps {
  items: NavItem[];
  activeGroupId: string | null;
  collapsed: boolean;
  onGroupSelect: (id: string) => void;
}

const iconMap: Record<string, Icons.LucideIcon> = {
  LayoutDashboard: Icons.LayoutDashboard,
  LayoutGrid: Icons.LayoutGrid,
  Users: Icons.Users,
  Table: Icons.Table,
  IndianRupee: Icons.IndianRupee,
  BarChart3: Icons.BarChart3,
  Calendar: Icons.Calendar,
  LifeBuoy: Icons.LifeBuoy,
  Settings: Icons.Settings,
  Package: Icons.Package,
  Layers: Icons.Layers,
  Store: Icons.Store,
  Warehouse: Icons.Warehouse,
  ShoppingCart: Icons.ShoppingCart,
  UserCog: Icons.UserCog,
  Tag: Icons.Tag,
  Truck: Icons.Truck,
  Receipt: Icons.Receipt,
  Calculator: Icons.Calculator,
};

export default function IconRail({ items, activeGroupId, collapsed, onGroupSelect }: IconRailProps) {
  const t = (k: string) => k.split('.').pop() ?? k;
  const location = useLocation();
  const navigate = useNavigate();
  const signOut = useSignOut();
  const [signingOut, setSigningOut] = useState(false);

  /* Logout from the icon rail (Bug ref: AI Agent — "add logout icon
     in mini bar"). Lives at the bottom of the rail so it never
     competes with the nav items above. Calls `useSignOut` so the
     server-side session is revoked, then routes home. */
  const handleLogout = async () => {
    setSigningOut(true);
    try {
      await signOut();
    } finally {
      setSigningOut(false);
      navigate('/', { replace: true });
    }
  };

  return (
    <div className="flex h-full w-16 flex-col border-r border-border bg-surface-sidebar">
      <nav className="flex flex-1 flex-col gap-1 overflow-y-auto px-2 py-3">
        {items.map((item) => {
          const Icon = iconMap[item.icon] || Icons.Circle;
          const isActive = activeGroupId === item.id;
          const isCurrentRoute = item.path && location.pathname === item.path;

          return (
            <button
              key={item.id}
              title={t(item.labelKey)}
              onClick={() => {
                onGroupSelect(item.id);
                if (item.path && !item.children?.length) {
                  navigate(item.path);
                }
              }}
              className={cn(
                'flex h-10 w-10 items-center justify-center rounded-lg transition-colors mx-auto',
                isActive || isCurrentRoute
                  ? 'bg-primary text-primary-foreground'
                  : 'text-content-muted hover:bg-surface-card-hover hover:text-content-primary'
              )}
            >
              <Icon size={20} />
            </button>
          );
        })}
      </nav>
      {/* Sign-out button pinned to the bottom of the rail. */}
      <div className="border-t border-border p-2">
        <button
          type="button"
          title={signingOut ? 'Signing out…' : 'Sign out'}
          aria-label="Sign out"
          onClick={handleLogout}
          disabled={signingOut}
          className={cn(
            'mx-auto flex h-10 w-10 items-center justify-center rounded-lg transition-colors',
            'text-content-muted hover:bg-danger-light hover:text-danger disabled:opacity-60',
          )}
        >
          <Icons.LogOut size={20} />
        </button>
      </div>
    </div>
  );
}
